import { Dialog, Tag, Text } from '@noorden-insurtech/alba-design-system';
import { ArrowRight } from 'lucide-react';
import type { QuoteActivity } from '@/services/quotes/activities';
import { formatDateTimeShortToBrazil } from '@/shared/utils/format-date.util';
import { getQuoteActivityColor } from './quote-activity.config';

interface QuoteActivityDetailDialogProps {
  activity: QuoteActivity | null;
  onClose: () => void;
}

const EMPTY = '—';

/**
 * Detalhe de uma atividade: exatamente o que mudou, no formato
 * `valor anterior → valor novo`.
 *
 * Os valores já chegam formatados em pt-BR do backend — aqui só posicionamos.
 */
export function QuoteActivityDetailDialog({
  activity,
  onClose,
}: QuoteActivityDetailDialogProps) {
  return (
    <Dialog.Root
      open={Boolean(activity)}
      onOpenChange={details => {
        if (!details.open) onClose();
      }}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='flex max-h-[80vh] w-[680px] max-w-[90vw] flex-col'>
          <Dialog.Header>
            <Dialog.Title>Detalhe da atividade</Dialog.Title>
          </Dialog.Header>

          {activity && (
            <Dialog.Body className='flex-1 overflow-y-auto px-6 pb-6'>
              <div className='mb-4 flex flex-wrap items-center gap-3 border-b border-alba-gray-100 pb-4'>
                <Tag.Root
                  variant='solid'
                  colorScheme={getQuoteActivityColor(activity.event)}
                  size='sm'
                >
                  <Tag.Label>{activity.eventLabel}</Tag.Label>
                </Tag.Root>
                <Text className='text-sm text-alba-black-500'>
                  {activity.subjectLabel}
                </Text>
                <Text className='text-sm text-alba-gray-500'>
                  {formatDateTimeShortToBrazil(activity.occurredAt)}
                </Text>
                <Text className='text-sm text-alba-gray-500'>
                  {activity.actorName ?? EMPTY}
                </Text>
              </div>

              {activity.description && (
                <Text className='mb-4 block text-sm text-alba-black-500'>
                  {activity.description}
                </Text>
              )}

              {activity.changes.length === 0 ? (
                <Text className='text-sm text-alba-gray-500'>
                  Esta atividade não registrou alteração de campos.
                </Text>
              ) : (
                <div className='flex flex-col'>
                  {activity.changes.map((change, index) => (
                    <div
                      key={`${change.label}-${index}`}
                      className='grid grid-cols-1 gap-1 border-b border-alba-gray-100 py-3 sm:grid-cols-[1fr_auto_1fr] sm:items-center sm:gap-2'
                    >
                      <span className='col-span-full mb-1 text-xs font-medium text-alba-gray-500'>
                        {change.label}
                      </span>
                      <span className='rounded-md bg-alba-gray-50 px-2 py-1 text-sm text-alba-gray-600'>
                        {change.before ?? EMPTY}
                      </span>
                      <ArrowRight className='hidden h-4 w-4 text-alba-gray-400 sm:block' />
                      <span className='rounded-md bg-alba-blue-50 px-2 py-1 text-sm font-medium text-alba-blue-700'>
                        {change.after ?? EMPTY}
                      </span>
                      {change.sampleLabels &&
                        change.sampleLabels.length > 0 && (
                          <span className='col-span-full mt-1 text-xs text-alba-gray-500'>
                            Ex.: {change.sampleLabels.join(', ')}
                          </span>
                        )}
                    </div>
                  ))}
                </div>
              )}

              {activity.changesTruncated && (
                <Text className='mt-3 block text-xs text-alba-gray-500'>
                  Exibindo {activity.changes.length} de {activity.changesTotal}{' '}
                  alterações.
                </Text>
              )}
            </Dialog.Body>
          )}
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
